import { useEffect, useState } from "react";
import { Check, ExternalLink, FileText } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { LADDER, LOGI_BOOL, LOGI_TXT, PARCHEGGI, TIPOLOGIE, fmtDateTime, yesNo } from "@/lib/format";
import { Chip, Field } from "@/components/rp";

export const EmailLog = ({ refId, reload }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/email-log", { params: { ref_id: refId } }).then((r) => setItems(r.data)).catch(() => setItems([])), 1500);
    return () => clearTimeout(t);
  }, [refId, reload]);
  return (
    <Section label="Email al cliente" testid="email-log-section">
      {!items?.length ? <p className="text-sm text-[#757575]">Nessuna email inviata.</p> : (
        <div className="space-y-3 max-h-[320px] overflow-y-auto pr-2">
          {items.map((e) => (
            <div key={e.id} className="text-sm border-l-2 border-[#EBEBEB] pl-3" data-testid={`email-log-${e.id}`}>
              <p className="font-medium">{e.subject}</p>
              <p className="text-xs text-[#757575]">{fmtDateTime(e.at)} · {e.to}</p>
              <p className={`text-xs ${e.sent ? "text-[#198754]" : e.sent === false ? "text-[#DC3545]" : "text-[#757575]"}`}>
                {e.sent ? "Accettata dal servizio email" : e.sent === false ? `Non inviata — ${e.error || "errore"}` : "In invio…"}
              </p>
              {e.sent && <p className={`text-xs ${e.read_at ? "text-[#198754]" : "text-[#757575]"}`} data-testid={`email-read-${e.id}`}>{e.read_at ? `Lettura confermata il ${fmtDateTime(e.read_at)}` : "Lettura non ancora confermata"}</p>}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

export const Section = ({ label, children, action, className = "", testid }) => (
  <section className={`rp-card p-5 sm:p-6 ${className}`} data-testid={testid}>
    <div className="flex items-center justify-between gap-3 mb-4">
      <p className="rp-label">{label}</p>
      {action}
    </div>
    {children}
  </section>
);

export const Info = ({ label, children }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-[#757575]">{label}</p>
    <div className="mt-1 text-[15px]">{children || "—"}</div>
  </div>
);

export const InternalNote = ({ text }) => (
  <div className="rounded-2xl bg-[#E6DDCE] p-4" data-testid="internal-note">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-black/60 mb-1">Interno — visibile solo allo staff</p>
    <p className="text-sm whitespace-pre-wrap">{text || "Nessuna nota interna."}</p>
  </div>
);

export const AccessData = ({ x }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="access-data">
    <Info label="Piano">{x.delivery_floor}</Info>
    <Info label="Ascensore">{yesNo(x.elevator)}</Info>
    <Info label="ZTL">{yesNo(x.ztl)}</Info>
    <Info label="Scala aerea">{LADDER[x.aerial_ladder]}</Info>
    <LogisticaInfo l={x.logistica} />
  </div>
);

export const LogisticaInfo = ({ l }) => {
  if (!l) return null;
  const yes = Object.entries(LOGI_BOOL).filter(([k]) => l[k] !== undefined).map(([k, t]) => `${t}: ${l[k] ? "sì" : "no"}`);
  return (
    <>
      {l.tipologia && <Info label="Tipologia">{TIPOLOGIE[l.tipologia]}</Info>}
      {l.parcheggio && <Info label="Parcheggio">{PARCHEGGI[l.parcheggio]}</Info>}
      {yes.length > 0 && <div className="col-span-2 sm:col-span-4 text-sm" data-testid="logistica-flags">{yes.join(" · ")}</div>}
      {Object.entries(LOGI_TXT).filter(([k]) => l[k]).map(([k, t]) => <div key={k} className="col-span-2"><Info label={t}>{l[k]}</Info></div>)}
    </>
  );
};

const YesNo = ({ value, onChange, id }) => (
  <div className="flex gap-2">
    <Chip active={value === true} onClick={() => onChange(true)} data-testid={`${id}-yes`}>Sì</Chip>
    <Chip active={value === false} onClick={() => onChange(false)} data-testid={`${id}-no`}>No</Chip>
  </div>
);

const Tri = ({ value, onChange, id }) => (
  <div className="flex gap-2">
    {[[true, "Sì"], [false, "No"]].map(([v, t]) => <Chip key={t} active={value === v} onClick={() => onChange(value === v ? undefined : v)} data-testid={`${id}-${v ? "yes" : "no"}`}>{t}</Chip>)}
  </div>
);

export const LogisticaExtra = ({ l = {}, onChange }) => {
  const [open, setOpen] = useState(Object.keys(l).length > 0);
  const up = (k, v) => { const n = { ...l, [k]: v }; if (v === undefined || v === "") delete n[k]; onChange(n); };
  return (
    <div className="space-y-4" data-testid="logistica-extra">
      <button type="button" onClick={() => setOpen(!open)} className="text-sm font-medium underline" data-testid="logistica-toggle">{open ? "Nascondi" : "Aggiungi"} info su abitazione e logistica (facoltative)</button>
      {open && <>
        <div className="space-y-2"><p className="rp-label">Tipologia abitazione</p>
          <div className="flex flex-wrap gap-2">{Object.entries(TIPOLOGIE).map(([v, t]) => <Chip key={v} active={l.tipologia === v} onClick={() => up("tipologia", l.tipologia === v ? undefined : v)} data-testid={`logi-tipologia-${v}`}>{t}</Chip>)}</div>
        </div>
        <div className="space-y-2"><p className="rp-label">Parcheggio mezzi</p>
          <div className="flex flex-wrap gap-2">{Object.entries(PARCHEGGI).map(([v, t]) => <Chip key={v} active={l.parcheggio === v} onClick={() => up("parcheggio", l.parcheggio === v ? undefined : v)} data-testid={`logi-parcheggio-${v}`}>{t}</Chip>)}</div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {Object.entries(LOGI_BOOL).map(([k, t]) => <div key={k} className="space-y-2"><p className="rp-label">{t}</p><Tri value={l[k]} onChange={(v) => up(k, v)} id={`logi-${k}`} /></div>)}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {Object.entries(LOGI_TXT).map(([k, t]) => <Field key={k} label={t}><input value={l[k] || ""} onChange={(e) => up(k, e.target.value)} className="rp-field bg-white" data-testid={`logi-${k}`} /></Field>)}
        </div>
      </>}
    </div>
  );
};

export const AccessFields = ({ f, set, optional = false }) => (
  <fieldset className="rounded-2xl border border-[#EBEBEB] bg-[#F7F7F7] p-4 space-y-4">
    <legend className="px-2 text-sm font-medium">Dati di accesso al cantiere <span className="text-[#757575] font-normal">— {optional ? "facoltativi" : "obbligatori"}</span></legend>
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Piano"><input value={f.delivery_floor || ""} onChange={(e) => set("delivery_floor", e.target.value)} className="rp-field bg-white" placeholder="Es. 3° piano, piano terra" data-testid="access-floor-input" /></Field>
      <div className="space-y-2"><p className="rp-label">Ascensore</p><YesNo value={f.elevator} onChange={(v) => set("elevator", v)} id="access-elevator" /></div>
      <div className="space-y-2"><p className="rp-label">ZTL</p><YesNo value={f.ztl} onChange={(v) => set("ztl", v)} id="access-ztl" /></div>
      <div className="space-y-2"><p className="rp-label">Scala aerea</p>
        <div className="flex flex-wrap gap-2">{Object.entries(LADDER).map(([v, l]) => <Chip key={v} active={f.aerial_ladder === v} onClick={() => set("aerial_ladder", v)} data-testid={`access-ladder-${v}`}>{l}</Chip>)}</div>
      </div>
    </div>
    <LogisticaExtra l={f.logistica || {}} onChange={(v) => set("logistica", v)} />
  </fieldset>
);

const FLAT = ["delivery_floor", "elevator", "ztl", "aerial_ladder"];
export const ClientLogiFields = ({ l = {}, onChange }) => {
  const flat = Object.fromEntries(FLAT.filter((k) => k in l).map((k) => [k, l[k]]));
  const extra = Object.fromEntries(Object.entries(l).filter(([k]) => !FLAT.includes(k)));
  return <AccessFields optional f={{ ...flat, logistica: extra }} set={(k, v) => onChange(k === "logistica" ? { ...flat, ...v } : { ...l, [k]: v })} />;
};


export const DocItem = ({ d, href, children }) => (
  <div className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-[#EBEBEB]" data-testid={`doc-item-${d.id}`}>
    <FileText className="h-5 w-5 shrink-0" strokeWidth={1.5} />
    <a href={fileUrl(href)} target="_blank" rel="noreferrer" className="flex-1 min-w-0 hover:underline">
      <span className="block text-sm font-medium truncate">{d.filename}</span>
      <span className="block text-xs text-[#757575]">{fmtDateTime(d.created_at)}{d.uploaded_by_name && ` · ${d.uploaded_by_name}`}</span>
    </a>
    {children}
    <a href={fileUrl(href)} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 text-[#757575]" /></a>
  </div>
);

export const Timeline = ({ steps }) => (
  <ol className="relative" data-testid="order-timeline">
    {steps.map((s, i) => {
      const done = s.state === "completata";
      const cur = s.state === "attuale";
      return (
        <li key={s.code} className="flex gap-4 pb-5 last:pb-0 relative" data-testid={`timeline-step-${s.code}`}>
          {i < steps.length - 1 && <span className={`absolute left-[13px] top-7 bottom-0 w-px ${done ? "bg-[#070707]" : "bg-[#EBEBEB]"}`} />}
          <span className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold z-[1] ${done ? "bg-[#070707] text-white" : cur ? "bg-[#C8BDAB] text-black ring-4 ring-[#E6DDCE]" : "bg-[#EBEBEB] text-[#757575]"}`}>
            {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
          </span>
          <div className="min-w-0">
            <p className={`text-sm ${cur ? "font-semibold" : done ? "font-medium" : "text-[#757575]"}`}>{s.label}</p>
            <p className="text-xs text-[#757575]">
              {done ? `Completata · ${fmtDateTime(s.reached_at)}` : cur ? `In corso${s.reached_at ? ` · ${fmtDateTime(s.reached_at)}` : ""}` : s.state === "non_registrata" ? "Non registrata" : "Da fare"}
            </p>
            {cur && <p className="text-xs mt-1">{s.description}</p>}
          </div>
        </li>
      );
    })}
  </ol>
);
